import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users, MessageSquare, Search, Eye, FileDown, ChevronDown, ArrowUp } from 'lucide-react';
import { FeedbackRequest } from '../types/feedback';
import { UserProfile } from '../types/userProfile';
import { useApiClient } from '../hooks/useApiClient';
import { getStatusColor, getStatusIcon } from '../utils/feedbackUtils';
import { YEARS } from '../config/feedbackConfig';
import { Pagination } from './common/Pagination';
import { generateConnectPDFContent, printHtmlInNewWindow } from '../utils/pdfTemplates';
import { formatDateInIST } from '../utils/mappers';

const ITEMS_PER_PAGE = 20;

interface TeamFeedbackViewProps {
  userProfile: UserProfile | null;
  requests: FeedbackRequest[];
  requestsLoading: boolean;
  onRefresh?: () => void;
}

export function TeamFeedbackView({ userProfile, requests, requestsLoading, onRefresh }: TeamFeedbackViewProps) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters
  const [filterYear, setFilterYear] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchConnectName, setSearchConnectName] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const { apiCall } = useApiClient();

  // Memoized unique employees for filter
  const uniqueEmployees = useMemo(() => 
    Array.from(new Set(requests.map(r => r.employeeName))).sort(),
    [requests]
  );

  // Memoized filtered requests (excludes drafts — drafts are not visible to manager)
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      if (request.status === 'draft') return false;
      const yearMatch = filterYear === 'all' || request.year === parseInt(filterYear);
      const employeeMatch = filterEmployee === 'all' || request.employeeName === filterEmployee;
      const statusMatch = filterStatus === 'all' || request.status === filterStatus;
      const connectNameMatch = !searchConnectName || 
        (request.connectName && request.connectName.toLowerCase().includes(searchConnectName.toLowerCase()));
      return yearMatch && employeeMatch && statusMatch && connectNameMatch;
    });
  }, [requests, filterYear, filterEmployee, filterStatus, searchConnectName]);

  // Memoized pagination
  const totalPages = useMemo(() => 
    Math.ceil(filteredRequests.length / ITEMS_PER_PAGE),
    [filteredRequests.length]
  );
  
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredRequests, currentPage]);

  // Memoized status counts for summary
  const statusCounts = useMemo(() => ({
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    revision_requested: requests.filter(r => r.status === 'revision_requested').length,
    completed: requests.filter(r => r.status === 'completed').length,
  }), [requests]);

  // Scroll-to-top
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(
    () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    []
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterYear, filterEmployee, filterStatus, searchConnectName, requests.length]);

  const handleViewRequest = async (request: FeedbackRequest) => {
    // Always open manager review page for team connects
    navigate(`/provide-manager-review/${request.id}`);
  };

  const handleExportPDF = async (request: FeedbackRequest) => {
    if (request.status !== 'completed') return;
    
    try {
      // Fetch full request details
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${request.id}`
      );
      if (!response.ok) {
        console.error('Failed to fetch request details');
        return;
      }
      const result = await response.json();
      const fullRequest: FeedbackRequest = {
        ...result.data,
        answers: result.data.answers || []
      };

      // Fetch core priorities
      let corePriorities = null;
      try {
        const prioritiesResponse = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(request.employeeId)}/year/${request.year}`
        );
        if (prioritiesResponse.ok) {
          const prioritiesResult = await prioritiesResponse.json();
          if (prioritiesResult.data) {
            corePriorities = prioritiesResult.data;
          }
        }
      } catch (err) {
        console.log('No core priorities found');
      }

      const pdfContent = generateConnectPDFContent(fullRequest, corePriorities);
      printHtmlInNewWindow(pdfContent);
    } catch (err) {
      console.error('Error exporting PDF:', err);
    }
  };

  if (requestsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
          <p className="text-slate-600 font-medium">Loading team connect requests...</p>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center">
        <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h3 className="text-xl font-bold text-slate-900 mb-2">No Team Connect Requests</h3>
        <p className="text-slate-500 max-w-md mx-auto">
          Your direct reports haven't submitted any connect requests yet. 
          Once they do, you'll be able to review and provide feedback here.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-900">Team Connect Overview</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setFilterStatus(filterStatus === 'all' ? 'all' : 'all')}
          className={`bg-white rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer hover:shadow-md ${
            filterStatus === 'all'
              ? 'border-slate-400 ring-2 ring-slate-200'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Requests</p>
              <p className="text-2xl font-bold text-slate-900">{requests.filter(r => r.status !== 'draft').length}</p>
            </div>
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-slate-600" />
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setFilterStatus(filterStatus === 'in_progress' ? 'all' : 'in_progress')}
          className={`bg-white rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer hover:shadow-md ${
            filterStatus === 'in_progress'
              ? 'border-blue-400 ring-2 ring-blue-100'
              : 'border-slate-200 hover:border-blue-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{statusCounts.in_progress}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setFilterStatus(filterStatus === 'revision_requested' ? 'all' : 'revision_requested')}
          className={`bg-white rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer hover:shadow-md ${
            filterStatus === 'revision_requested'
              ? 'border-orange-400 ring-2 ring-orange-100'
              : 'border-slate-200 hover:border-orange-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Revision Requested</p>
              <p className="text-2xl font-bold text-orange-600">{statusCounts.revision_requested}</p>
            </div>
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full" />
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setFilterStatus(filterStatus === 'completed' ? 'all' : 'completed')}
          className={`bg-white rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer hover:shadow-md ${
            filterStatus === 'completed'
              ? 'border-green-400 ring-2 ring-green-100'
              : 'border-slate-200 hover:border-green-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{statusCounts.completed}</p>
            </div>
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
            </div>
          </div>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          {/* Connect Name Search */}
          <div className="relative">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Connect</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchConnectName}
                onChange={(e) => setSearchConnectName(e.target.value)}
                placeholder="Search..."
                className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Employee Filter */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Employee</label>
            <div className="relative">
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
              >
                <option value="all">All Employees</option>
                {uniqueEmployees.map(emp => (
                  <option key={emp} value={emp}>{emp}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Year</label>
            <div className="relative">
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
              >
                <option value="all">All Years</option>
                {YEARS.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
            <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
            >
              <option value="all">All Statuses</option>
              <option value="in_progress">In Progress</option>
              <option value="revision_requested">Revision Requested</option>
              <option value="completed">Completed</option>
            </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Request Count */}
          <div className="flex items-center justify-end">
            <span className="text-sm font-medium text-slate-500">
              {filteredRequests.length} {filteredRequests.length === 1 ? 'Request' : 'Requests'}
            </span>
          </div>
        </div>
      </div>

      {/* Team Requests Table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Connect</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Employee</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Date</th>
                <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Status</th>
                <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedRequests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="text-slate-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="font-bold text-lg">No requests found</p>
                      <p className="text-sm mt-1">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-pink-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 align-middle">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900">{request.connectName || request.employeeName}</div>
                        {request.pip?.isOnPip && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200">
                            PIP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-600 text-sm">
                      {request.employeeName}
                      {userProfile?.employeeId && request.managerEmployeeId !== userProfile.employeeId && (
                        <div className="text-[11px] text-amber-600 mt-0.5">
                          Originally assigned to: {request.managerName}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-500 text-sm">
                      {formatDateInIST(request.requestDate)}
                    </td>
                    <td className="px-6 py-4 align-middle text-center">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status.replace('_', ' ').toUpperCase()}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle text-center">
                      <div className="inline-flex items-center gap-1.5">
                        {/* View/Review — always available */}
                        <button
                          onClick={() => handleViewRequest(request)}
                          title="Review"
                          className="p-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all shadow-sm shadow-pink-500/20"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* Export PDF — only for completed */}
                        {request.status === 'completed' && (
                          <button
                            onClick={() => handleExportPDF(request)}
                            title="Export PDF"
                            className="p-2 bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200 rounded-lg transition-colors"
                          >
                            <FileDown className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredRequests.length}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      </div>

      {/* Back to Top FAB */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
          bg-gradient-to-r from-pink-500 to-purple-600 text-white
          shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
          flex items-center justify-center
          transition-all duration-300 hover:scale-110
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </>
  );
}
